import { css } from "@emotion/react";
import { Flex, Text } from "@phoenix/components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";

export function ProjectPerformanceChartCard({
  metric,
  setMetric,
  chartData,
  chartCardCSS,
  costFormatter,
  fmtLatency,
  Custom3DBar,
}: {
  metric: "cost" | "latency" | "tokens" | "traces";
  setMetric: (m: "cost" | "latency" | "tokens" | "traces") => void;
  chartData: any[];
  chartCardCSS: any;
  costFormatter: (val: number) => string;
  fmtLatency: (val: number) => string;
  Custom3DBar: any;
}) {
  return (
    <div css={chartCardCSS}>
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="size-150"
      >
        <div
          css={css`
            font-size: 22px;
            font-weight: 900;
            color: #000000 !important;
            letter-spacing: -0.3px;
          `}
        >
          Project Performance Comparison
        </div>
        <Flex
          direction="row"
          gap="size-50"
          css={css`
            background: var(--global-color-gray-200);
            padding: 3px;
            border-radius: 8px;
            border: 1px solid var(--global-border-color-default);
          `}
        >
          {(["traces", "cost", "latency", "tokens"] as const).map((mKey) => (
            <button
              key={mKey}
              onClick={() => setMetric(mKey)}
              css={css`
                border: none;
                background: ${metric === mKey
                  ? "var(--global-color-gray-50)"
                  : "transparent"};
                color: ${metric === mKey ? "#00685f" : "#000000"};
                font-size: 13px;
                font-weight: 900;
                padding: 5px 14px;
                border-radius: 6px;
                cursor: pointer;
                text-transform: capitalize;
                transition: all 0.3s ease;
                box-shadow: ${metric === mKey
                  ? "0 1px 3px rgba(0,0,0,0.1)"
                  : "none"};
                &:hover {
                  background: ${metric === mKey
                    ? "var(--global-color-gray-50)"
                    : "var(--global-color-gray-100)"};
                }
              `}
            >
              {mKey}
            </button>
          ))}
        </Flex>
      </Flex>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 15, right: 30, left: 10, bottom: 5 }}
          >
            <XAxis
              type="number"
              stroke="#475569"
              fontSize={11}
              tick={{ fill: "#334155", fontWeight: 600 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(val) =>
                metric === "cost"
                  ? costFormatter(val)
                  : metric === "latency"
                    ? fmtLatency(val)
                    : metric === "tokens"
                      ? val >= 1_000_000
                        ? `${(val / 1_000_000).toFixed(1)}M`
                        : val >= 1000
                          ? `${(val / 1000).toFixed(0)}K`
                          : val.toLocaleString()
                      : val.toLocaleString()
              }
            />
            <YAxis
              dataKey="name"
              type="category"
              stroke="#0f172a"
              fontSize={12}
              fontWeight={700}
              tick={{ fill: "#0f172a", fontWeight: 700 }}
              tickLine={false}
              axisLine={false}
              width={110}
            />
            <Tooltip
              cursor={{ fill: "var(--global-color-gray-200)", opacity: 0.12 }}
              formatter={((value: number) => [
                metric === "cost"
                  ? costFormatter(value)
                  : metric === "latency"
                    ? fmtLatency(value)
                    : metric === "tokens"
                      ? value >= 1_000_000
                        ? `${(value / 1_000_000).toFixed(2)}M tokens`
                        : value >= 1000
                          ? `${(value / 1000).toFixed(1)}K tokens`
                          : `${value.toLocaleString()} tokens`
                      : value.toLocaleString(),
                metric === "cost"
                  ? "Cost"
                  : metric === "latency"
                    ? "Latency (P50)"
                    : metric === "tokens"
                      ? "Tokens"
                      : "Traces",
              ]) as any}
              contentStyle={{
                background: "var(--global-color-gray-75)",
                border: "1px solid var(--global-border-color-default)",
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
              }}
              itemStyle={{
                color: "var(--global-text-color-900)",
              }}
              labelStyle={{
                color: "var(--global-text-color-900)",
              }}
            />
            <Bar
              dataKey="value"
              barSize={20}
              shape={(props: any) => <Custom3DBar {...props} />}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          flex="1"
        >
          <Text size="S" color="text-500">
            {metric === "cost"
              ? "No cost data recorded in this time range."
              : metric === "latency"
                ? "No latency data recorded in this time range."
                : metric === "tokens"
                  ? "No token data recorded in this time range."
                  : "No trace data recorded in this time range."}
          </Text>
        </Flex>
      )}
    </div>
  );
}
